import { useQuery } from "@tanstack/react-query";

import type {
	DummyDocumentData,
	DummyExtendedContract,
} from "AdvisoryManagement/types";
import { getRandomValue } from "AdvisoryManagement/Utils/random";
import {
	type ContractDocumentResponse,
	ContractExpandFields,
	type ContractStatus,
	type DocumentResponse,
	type EntityListOfContractResponse,
	getContracts,
	ProductType,
} from "Infrastructure/Api/Api";
import { processResponse } from "Infrastructure/Api/ApiUtils";
import keys from "../keys";

export const useContractsQuery = (
	advisoryCaseId: string | null | undefined,
	contractStatus: ContractStatus[],
) => {
	return useQuery({
		queryKey: keys.contracts(advisoryCaseId, contractStatus),
		queryFn: processResponse(() =>
			getContracts(undefined, undefined, advisoryCaseId, contractStatus, [
				ContractExpandFields.Documents,
				ContractExpandFields.Checks,
			]),
		),
		select: (data: EntityListOfContractResponse) => {
			return {
				...data,
				items: data.items.map((c) => getExtendedContractData(c)),
			};
		},
		enabled: !!advisoryCaseId,
	});
};

const getExtendedContractData = (
	contract: EntityListOfContractResponse["items"][number],
) => {
	const extendedContract: DummyExtendedContract = {
		contractId: contract.contractId,
		advisoryCaseId: contract.advisoryCaseId,
		categoryCode: contract.categoryCode || ProductType.Insurance,
		institutionCode:
			contract.partnerCode ||
			getRandomValue([
				"Allianz",
				"Generali",
				"Axa",
				"Colonnade",
				"Groupama",
				"Kooperativa",
			]),
		productCode:
			contract.productCode ||
			getRandomValue([
				"Investiční produkt Basic",
				"Stavební spoření Plus",
				"Byt Standard",
			]),
		amount: getRandomValue([1500, 2500, 3500, 4500, 5500, 6300, 7200]),
		amountType: getRandomValue(["Monthly", "Yearly", "OneTime"]),
		essentials: {
			aml:
				(contract.essentials
					?.aml as DummyExtendedContract["essentials"]["aml"]) ?? "NotReady",
			provision:
				(contract.essentials
					?.provision as DummyExtendedContract["essentials"]["provision"]) ??
				"NotReady",
			control:
				(contract.essentials
					?.control as DummyExtendedContract["essentials"]["control"]) ??
				"NotReady",
		},
		status: getRandomValue(["Active", "Terminated", "Expired", "Draft"]),
		documents: [
			...(contract.documents?.map((d) => getExtendedDocumentData(d)) || []),
		],
	};
	return extendedContract;
};

export function getExtendedDocumentData(
	document: DocumentResponse,
): DummyDocumentData;
export function getExtendedDocumentData(
	document: ContractDocumentResponse,
): DummyDocumentData;
export function getExtendedDocumentData(
	document: DocumentResponse | ContractDocumentResponse,
): DummyDocumentData {
	const extendedDocument: DummyDocumentData = {
		documentId: document.documentId,
		dmsDocumentId: document.dmsDocumentId,
		documentName: getRandomValue([
			"Smlouva o pojištění bytu",
			"Obecné pojistné podmínky",
			"Záznam z jednání",
			"Prohlášení klienta",
			"Žádost o úvěr",
		]),
		documentType: document.documentType,
		status: getRandomValue(["New", "Pending", "Processed", "Error"]),
		controlStatus: getRandomValue(["NotReady", "Pending", "Ready"]),
		printStatus: getRandomValue(["NotReady", "Pending", "Ready"]),
		documentData:
			"documentData" in document ? document.documentData : undefined,
	};

	return extendedDocument;
}
